import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { ThemeProvider } from 'styled-components';
import { Button, Alert, ConfigProvider, TabsProps } from 'antd';
import type { AppDispatch, RootState } from '../../redux/store';
import {
  fetchUserProfile,
  updateUserProfile,
  setupTwoFactor,
  confirmTwoFactor,
  resendTwoFactor,
  disableTwoFactor,
  clearTwoFactorSetup,
} from '../../redux/Settings';
import type { UpdateProfilePayload } from '../../redux/Settings';
import { Container, HeaderSection, Title, Subtitle, StyledTabs } from './Styles';
import { useTheme } from '../../context/ThemeContext';
import { getAntdThemeConfig } from '../../utils/antdTheme';
import { notification } from '../../commonComponents/Notification/Notification';
import { validateFormData, validationRules, parseLoyaltyRatio } from './constants';
import ProfileSection from './ProfileSection';
import PersonalInformationSection from './PersonalInformationSection';
import AddressSection from './AddressSection';
import TaxConfigurationSection from './TaxConfigurationSection';
import PreferencesSection from './PreferencesSection';
import TwoStepVerificationSection from './TwoStepVerificationSection';
import AccountDangerSection from './AccountDangerSection';
import type { SettingsProps, TwoFactorMethod, FormData } from './types';

const Settings: React.FC<SettingsProps> = ({ role }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useTheme();
  const theme = useMemo<{ mode: 'light' | 'dark' }>(
    () => ({ mode: isDarkMode ? 'dark' : 'light' }),
    [isDarkMode]
  );
  const {
    user,
    loading,
    error,
    twoFactorSetup,
  } = useSelector((state: RootState) => state.settings);

  const { handleSubmit, reset, watch, setValue } = useForm<FormData>({
    defaultValues: {
      name: '',
      username: '',
      address: '',
      email: '',
      phone: '',
      publicPhone: '',
      tax: 0,
      loyaltyEnabled: false,
      loyaltyRedeemRatio: '',
      loyaltySpendRatio: '',
      receiveNotifications: false,
      autoRenewSubscription: false,
      twoFactorEnabled: false,
      twoFactorMethod: 'phone',
      twoFactorPhone: '',
      twoFactorEmail: '',
    },
  });

  const formData = watch();
  const [activeTab, setActiveTab] = useState('general');
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  

  useEffect(() => {
    dispatch(fetchUserProfile({ role }));
  }, [dispatch, role]);

  useEffect(() => {
    if (user) {
      reset({
        name: user.name || '',
        username: user.username || '',
        address: user.address || '',
        email: user.email || '',
        phone: user.phone || '',
        publicPhone: user.publicPhone || '',
        loyaltyEnabled: user.loyaltyEnabled || false,
        loyaltyRedeemRatio: user.loyaltyRedeemRatio || '',
        loyaltySpendRatio: user.loyaltySpendRatio || '',
        tax:
          user.tax !== undefined && user.tax !== null && !isNaN(user.tax)
            ? user.tax * 100
            : 0,
        receiveNotifications: user.receiveNotifications || false,
        autoRenewSubscription: user.autoRenewSubscription || false,
        twoFactorEnabled: user.twoFactorEnabled || false,
        twoFactorMethod: (user.twoFactorMethod || 'phone') as TwoFactorMethod,
        twoFactorPhone: user.twoFactorPhone || '',
        twoFactorEmail: user.twoFactorEmail || '',
      });
    }
  }, [user, reset]);

  const handleEdit = useCallback((section: string) => {
    setEditingSection(section);
  }, []);

  const onSave = useCallback(
    async (data: FormData) => {
      const fieldsToValidate = ['name', 'username', 'email', 'phone', 'address'];
      const errors = validateFormData(data, validationRules, fieldsToValidate);

      if (errors.length > 0) {
        notification.error(`Please fix ${errors.length} validation error(s).`);
        return;
      }

      if (!user) return;

      try {
        if (data.twoFactorEnabled) {
          if (data.twoFactorMethod === 'phone' && !data.twoFactorPhone?.trim()) {
            notification.error('Please enter your personal phone number for two-step verification.');
            return;
          }
          if (data.twoFactorMethod === 'email') {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!data.twoFactorEmail?.trim() || !emailRegex.test(data.twoFactorEmail.trim())) {
              notification.error('Please enter a valid personal email for two-step verification.');
              return;
            }
          }
        }

        const loyaltyRedeemNum = parseLoyaltyRatio(data.loyaltyRedeemRatio);
        const loyaltySpendNum = parseLoyaltyRatio(data.loyaltySpendRatio);

        const updatedData: UpdateProfilePayload = {
          name: data.name.trim(),
          username: data.username.trim(),
          address: data.address.trim(),
          email: data.email.trim(),
          phone: data.phone.trim(),
          publicPhone: data.publicPhone?.trim() || undefined,
          loyaltyEnabled: data.loyaltyEnabled,
          tax: data.tax / 100,
          receiveNotifications: data.receiveNotifications,
          autoRenewSubscription: data.autoRenewSubscription,
          ...(loyaltyRedeemNum !== undefined && { loyaltyRedeemRatio: loyaltyRedeemNum }),
          ...(loyaltySpendNum !== undefined && { loyaltySpendRatio: loyaltySpendNum }),
        };

        await dispatch(
          updateUserProfile({
            role,
            userId: user.id,
            data: updatedData,
            logoFile: logoFile || undefined,
            removeLogo: removeLogo,
          })
        ).unwrap();

        setLogoFile(null);
        setRemoveLogo(false);
        notification.success('Settings Updated', 'Your account settings have been updated successfully.');
        setEditingSection(null);
        setIsUploading(false);
      } catch (error: any) {
        const message = typeof error === 'string' ? error : error?.message || 'Failed to update settings.';
        notification.error(message);
      }
    },
    [dispatch, role, user, logoFile, removeLogo]
  );

  const handleCancel = useCallback(() => {
    reset();
    setLogoFile(null);
    setRemoveLogo(false);
    setEditingSection(null);
  }, [reset]);

  const handleRefresh = useCallback(() => {
    dispatch(fetchUserProfile({ role }));
  }, [dispatch, role]);

  const handleSetupTwoFactor = useCallback(async () => {
    if (!formData.twoFactorEnabled) {
      notification.error('Two-factor authentication is not enabled');
      return;
    }

    if (formData.twoFactorMethod === 'phone') {
      if (!formData.twoFactorPhone?.trim()) {
        notification.error('Please enter your personal phone number');
        return;
      }
    } else if (formData.twoFactorMethod === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!formData.twoFactorEmail?.trim() || !emailRegex.test(formData.twoFactorEmail.trim())) {
        notification.error('Please enter a valid personal email');
        return;
      }
    }

    try {
      const result = await dispatch(
        setupTwoFactor({
          method: formData.twoFactorMethod,
          twoFactorPhone: formData.twoFactorPhone?.trim(),
          twoFactorEmail: formData.twoFactorEmail?.trim(),
        })
      ).unwrap();

      notification.success('Verification Code Sent', result.message);
    } catch (error: any) {
      const message = typeof error === 'string' ? error : error?.message || 'Failed to setup 2FA';
      notification.error(message);
    }
  }, [dispatch, formData.twoFactorEnabled, formData.twoFactorMethod, formData.twoFactorPhone, formData.twoFactorEmail]);

  const handleConfirmTwoFactor = useCallback(async () => {
    if (!twoFactorCode.trim()) {
      notification.error('Please enter the verification code');
      return;
    }

    if (twoFactorCode.trim().length !== 6) {
      notification.error('Verification code must be 6 digits');
      return;
    }

    if (!twoFactorSetup.normalizedContact) {
      notification.error('Normalized contact not found. Please setup 2FA again.');
      return;
    }

    try {
      await dispatch(
        confirmTwoFactor({
          code: twoFactorCode.trim(),
          method: formData.twoFactorMethod,
          contact: twoFactorSetup.normalizedContact,
        })
      ).unwrap();

      notification.success('2FA Enabled', 'Two-factor authentication has been successfully enabled.');
      setTwoFactorCode('');
      setEditingSection(null);
      dispatch(clearTwoFactorSetup());
    } catch (error: any) {
      const message = typeof error === 'string' ? error : error?.message || 'Failed to confirm 2FA';
      notification.error(message);
    }
  }, [dispatch, twoFactorCode, formData.twoFactorMethod, twoFactorSetup.normalizedContact]);

  const handleResendTwoFactor = useCallback(async () => {
    if (!twoFactorSetup.normalizedContact) {
      notification.error('Contact not found. Please setup 2FA again.');
      return;
    }

    try {
      const result = await dispatch(
        resendTwoFactor({
          method: formData.twoFactorMethod,
          contact: twoFactorSetup.normalizedContact,
        })
      ).unwrap();

      notification.success('Code Resent', result.message);
      setTwoFactorCode('');
    } catch (error: any) {
      const message = typeof error === 'string' ? error : error?.message || 'Failed to resend code';
      notification.error(message);
    }
  }, [dispatch, formData.twoFactorMethod, twoFactorSetup.normalizedContact]);

  const handleDisableTwoFactor = useCallback(async () => {
    try {
      await dispatch(disableTwoFactor()).unwrap();
      notification.success('2FA Disabled', 'Two-factor authentication has been successfully disabled.');
      setTwoFactorCode('');
      dispatch(clearTwoFactorSetup());
    } catch (error: any) {
      const message = typeof error === 'string' ? error : error?.message || 'Failed to disable 2FA';
      notification.error(message);
    }
  }, [dispatch]);

  const handleDeactivateAccount = useCallback(async () => {
    try {
      // TODO: Integrate API endpoint for account deactivation
      notification.success('Account Deactivated', 'Your account has been temporarily deactivated. You can reactivate it anytime by logging in.');
    } catch (error: any) {
      const message = typeof error === 'string' ? error : error?.message || 'Failed to deactivate account';
      notification.error(message);
    }
  }, []);

  const handleDeleteAccount = useCallback(async () => {
    try {
      // TODO: Integrate API endpoint for account deletion
      notification.success('Account Deleted', 'Your account has been permanently deleted.');
    } catch (error: any) {
      const message = typeof error === 'string' ? error : error?.message || 'Failed to delete account';
      notification.error(message);
    }
  }, []);

  if (error && !user && !loading.profile) {
    return (
      <ThemeProvider theme={theme}>
        <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
          <Container>
            <Alert
              message="Error"
              description={error}
              type="error"
              showIcon
              action={
                <Button size="small" onClick={handleRefresh}>
                  Try Again
                </Button>
              }
            />
          </Container>
        </ConfigProvider>
      </ThemeProvider>
    );
  }

  const tabItems: TabsProps['items'] = [
    {
      key: 'general',
      label: 'General',
      children: (
        <div>
          <PersonalInformationSection
            user={user}
            editingSection={editingSection}
            formData={{
              name: formData.name,
              email: formData.email,
              phone: formData.phone,
            }}
            loading={loading.update}
            profileLoading={loading.profile}
            onEdit={() => handleEdit('personal')}
            onInputChange={(e) => setValue(e.target.name as keyof FormData, e.target.value)}
            onSave={handleSubmit(onSave)}
            onCancel={handleCancel}
          />
        </div>
      ),
    },
    {
      key: 'business',
      label: 'Business',
      children: (
        <div style={{ display: 'grid', gap: '24px', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
          <AddressSection
            user={user}
            editingSection={editingSection}
            formData={{
              address: formData.address,
              publicPhone: formData.publicPhone,
            }}
            loading={loading.update}
            profileLoading={loading.profile}
            onEdit={() => handleEdit('address')}
            onInputChange={(e) => setValue(e.target.name as keyof FormData, e.target.value)}
            onSave={handleSubmit(onSave)}
            onCancel={handleCancel}
          />

          <TaxConfigurationSection
            user={user}
            editingSection={editingSection}
            formData={{ tax: formData.tax }}
            loading={loading.update}
            profileLoading={loading.profile}
            onEdit={() => handleEdit('tax')}
            onNumberChange={(name, value) => setValue(name as keyof FormData, value)}
            onSave={handleSubmit(onSave)}
            onCancel={handleCancel}
          />
        </div>
      ),
    },
    {
      key: 'preferences',
      label: 'Preferences',
      children: (
        <PreferencesSection
          editingSection={editingSection}
          formData={{
            receiveNotifications: formData.receiveNotifications,
            autoRenewSubscription: formData.autoRenewSubscription,
          }}
          loading={loading.update}
          profileLoading={loading.profile}
          onEdit={() => handleEdit('additional')}
          onSwitchChange={(field, checked) => setValue(field as keyof FormData, checked)}
          onSave={handleSubmit(onSave)}
          onCancel={handleCancel}
        />
      ),
    },
    {
      key: 'security',
      label: 'Security',
      children: (
        <TwoStepVerificationSection
          editingSection={editingSection}
          formData={{
            twoFactorEnabled: formData.twoFactorEnabled,
            twoFactorMethod: formData.twoFactorMethod,
            twoFactorPhone: formData.twoFactorPhone,
            twoFactorEmail: formData.twoFactorEmail,
          }}
          profileLoading={loading.profile}
          setupLoading={loading.twoFactorSetup}
          twoFactorCode={twoFactorCode}
          twoFactorSetupInProgress={twoFactorSetup.inProgress}
          expiresAt={twoFactorSetup.expiresAt}
          onEdit={() => handleEdit('twoStep')}
          onSwitchChange={(field, checked) => setValue(field as keyof FormData, checked)}
          onInputChange={(e) => setValue(e.target.name as keyof FormData, e.target.value)}
          onMethodChange={(method) => setValue('twoFactorMethod', method)}
          onCancel={handleCancel}
          onSetupTwoFactor={handleSetupTwoFactor}
          onTwoFactorCodeChange={(code) => setTwoFactorCode(code)}
          onConfirmTwoFactor={handleConfirmTwoFactor}
          onResendTwoFactor={handleResendTwoFactor}
          onDisableTwoFactor={handleDisableTwoFactor}
        />
      ),
    },
    {
      key: 'account',
      label: 'Account',
      children: (
        <AccountDangerSection
          loading={loading.update}
          onDeactivate={handleDeactivateAccount}
          onDelete={handleDeleteAccount}
        />
      ),
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <Container>
          <HeaderSection>
            <div>
              <Title level={1}>Account Settings</Title>
              <Subtitle>Manage your profile and preferences</Subtitle>
            </div>
          </HeaderSection>

          <ProfileSection
            user={user}
            role={role}
            loading={loading.update}
            profileLoading={loading.profile}
            isUploading={isUploading}
            setIsUploading={setIsUploading}
            onEdit={() => handleEdit('profile')}
            onLogoChange={(file) => {
              setLogoFile(file);
              setRemoveLogo(file === null);
            }}
            onSaveProfile={handleSubmit(onSave)}
            onCancelProfile={handleCancel}
          />

          <StyledTabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
          />
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default React.memo(Settings);
